'use client';

import { Button, cn } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { SuspendedNotification } from '../my-account/SuspendedNotification/SuspendedNotification';
import { BalanceMenuItems } from '../my-account/Wallet/components/BalanceMenuItems/BalanceMenuItems';
import { TotalBalance } from '../my-account/Wallet/components/TotalBalance/TotalBalance';
import { useLatestTransactionSummary } from '../my-account/Wallet/hooks/useLatestTransactionSummary';
import { useWalletBalance } from '../my-account/Wallet/hooks/useWalletBalance';
import { useWalletBalanceHold } from '../my-account/Wallet/hooks/useWalletBalanceHold';

type BalanceBreakdownProps = {
  namespace: string;
  className?: string;
  onActionClick?: () => void;
};

function BalanceBreakdown({ namespace, className, onActionClick }: BalanceBreakdownProps) {
  const t = useTranslations(namespace);

  const { isWalletApiLockedFundsEnabled, suspendedNotificationNamespaces } = getSiteConfig().MY_ACCOUNT_SETTINGS;

  const { accountSummary, isLoading: isWalletTransactionsLoading } = useLatestTransactionSummary();
  const { totalBalance, withdrawableBalance, lockedBalance, isLoading: isWalletBalanceLoading } = useWalletBalance();
  const { availableBalance, cashBalance, bonusBalance, cashLinkedToBonus } = accountSummary || {};
  const { balance: totalBalanceToShow } = useWalletBalanceHold(totalBalance ?? availableBalance);
  const cashBalanceToShow = withdrawableBalance ?? cashBalance;
  const lockedFundsToShow = isWalletApiLockedFundsEnabled ? (lockedBalance ?? cashLinkedToBonus) : cashLinkedToBonus;
  const isBalanceLoading = isWalletTransactionsLoading || (isWalletBalanceLoading && !accountSummary);

  return (
    <div
      className={cn('flex flex-col gap-6', className)}
      data-track-feature="Navigation"
      data-track-container="Balance Breakdown"
    >
      <SuspendedNotification namespace={suspendedNotificationNamespaces.payments} variant="floating" />
      <TotalBalance
        namespace={namespace}
        availableBalance={totalBalanceToShow}
        className="bg-balance-breakdown-background text-balance-breakdown-foreground p-4 rounded-lg"
        onDepositClick={onActionClick}
      />
      <BalanceMenuItems
        namespace={namespace}
        className={className}
        cashBalance={cashBalanceToShow}
        bonusBalance={bonusBalance}
        lockedFunds={lockedFundsToShow}
        isWalletTransactionsLoading={isBalanceLoading}
      />
      <Button asChild variant="ghost" className="w-full" data-track-name="View Wallet">
        <Link href="/my-account/wallet" onClick={onActionClick}>
          {t('view_wallet')}
        </Link>
      </Button>
    </div>
  );
}

export { BalanceBreakdown };
